<template>
    <div>
        <input type="file" class="file-input" multiple @change="onChange" ref="files" />
        <div v-if="filesCount > 0">
            <div class="row no-gutters mb-3">
                <button class="col col-6 btn btn-primary" @click.prevent="uploadFiles()">
                    <i class="fa fa-cloud-upload-alt"></i>
                    {{ $t('[[[Wyślij]]]') }}
                </button>
                <button class="col col-6 btn btn-danger" @click.prevent="cancel()">
                    <i class="fa fa-ban"></i>
                    {{ $t('[[[Anuluj]]]') }}
                </button>
            </div>
            <b-progress :max="totalBytes" variant="primary" striped show-progress animated class="mb-3">
                <b-progress-bar :value="totalProgress" variant="primary"></b-progress-bar>
                <b-progress-bar :value="uploadProgress" variant="success"></b-progress-bar>
            </b-progress>
            <h6 class="border-bottom pb-2">{{ $t('[[[Pliki do wysłania: %0|||{0}]]]', filesCount) }}</h6>
            <responsive>
                <ul class="list-unstyled mb-0">
                    <li v-for="(file, index) in value" :key="`${index}.${file.name}`" class="d-flex p-1" :class="{'text-danger': isError(file), 'bg-white': index % 2 === 1}">
                        <span class="mr-1" v-if="uploading">
                            <i class="far fa-fw fa-clock text-muted" :title="$t('[[[Oczekuje na wysłanie]]]')" v-if="isWaiting(file)"></i>
                            <i class="fas fa-fw fa-clock text-primary" :title="$t('[[[Plik jest wysyłany]]]')" v-if="isSending(file)"></i>
                            <i class="fas fa-fw fa-check text-success" :title="$t('[[[Plik został wysłany]]]')" v-if="isSent(file)"></i>
                            <i class="fas fa-fw fa-exclamation-triangle text-danger" :title="$t('[[[Nie udało się wysłać pliku]]]')" v-if="isError(file)"></i>
                        </span>
                        <span class="mr-1" v-if="!uploading">
                            <i class="fas fa-fw fa-times text-danger pointer" :title="$t('[[[Usuń plik z listy]]]')" @click="remove(file)"></i>
                        </span>
                        <span class="mr-2">{{ file.name }}</span>
                        <span class="ml-auto text-nowrap">[{{ file.size | filesize }}]</span>
                    </li>
                </ul>
            </responsive>
        </div>
    </div>
</template>

<script>
import Storage from '@/services/core/StorageService';

export default {
    name: 'filemanager-upload',
    props: {
        'scope': {
            type: String,
            required: true,
            default: 'public'
        },
        'folder': {
            required: true
        },
        'value': {
            type: Array,
            required: true
        }
    },
    data()
    {
        return {
            current: null,
            uploading: false,
            totalProgress: 0,
            uploadProgress: 0,
            success: [],
            errors: []
        };
    },
    computed: {
        filesCount()
        {
            return this.value.length;
        },
        fileBytes()
        {
            return this.current ? this.current.size : 1;
        },
        totalBytes()
        {
            return this.value.reduce((sum, file) => sum + file.size, 0);
        }
    },
    methods: {
        isWaiting(file)
        {
            return !this.isSending(file) && !this.isSent(file) && !this.isError(file);
        },
        isSending(file)
        {
            return this.current === file;
        },
        isSent(file)
        {
            return this.success.filter(p => p === file).length > 0;
        },
        isError(file)
        {
            return this.errors.filter(p => p.file === file).length > 0;
        },
        openDialog()
        {
            this.$refs.files.click();
        },
        onChange(event)
        {
            let result = [];

            this.value.map(p => result.push(p));

            for (var i = 0; i < event.target.files.length; i++)
            {
                result.push(event.target.files.item(i));
            }

            this.$emit('input', result);
        },
        async uploadFiles()
        {
            this.uploading = true;
            this.totalProgress = 0;
            this.success = [];
            this.errors = [];

            for (let i = 0; i < this.value.length; i++)
            {
                if (this.uploading)
                {
                    let file = this.value[i];

                    try
                    {
                        await this.sendRequest(file);
                        this.success.push(file);
                    }
                    catch (ex)
                    {
                        this.errors.push({
                            file: file,
                            error: ex
                        });
                    }
                }
            }

            if (this.uploading)
            {
                this.$emit('input', this.errors.map(p => p.file));
                this.$emit('reload');
            }

            if (this.value.length === 0)
            {
                this.totalProgress = 0;
            }

            this.current = null;
            this.uploading = false;
        },
        async cancel()
        {
            if (this.uploading)
            {
                this.uploading = false;
            }
            else
            {
                this.success = [];
                this.errors = [];
                this.$emit('input', []);
            }
        },
        sendRequest(file)
        {
            this.current = file;
            this.uploadProgress = 0;

            return new Promise((resolve, reject) =>
            {
                const formData = new FormData();
                const req = new XMLHttpRequest();

                formData.append('file', file, file.name);
                formData.append('scope', this.scope);

                if (this.folder)
                {
                    formData.append('folder', this.folder);
                }

                req.upload.addEventListener('progress', (event) =>
                {
                    if (event.lengthComputable)
                    {
                        this.uploadProgress = event.loaded;
                    }
                });

                req.upload.addEventListener('load', (event) =>
                {
                    this.uploadProgress = 0;
                    this.totalProgress += file.size;
                    resolve(req.response);
                });

                req.upload.addEventListener('error', (event) =>
                {
                    this.uploadProgress = 0;
                    this.totalProgress += file.size;
                    reject(req.response);
                });

                req.open('POST', Storage.uploadUrl());
                req.setRequestHeader('Authorization', 'Bearer ' + this.$auth.token());
                req.send(formData);
            });
        },
        remove(file)
        {
            this.$emit('input', this.value.filter(p => p !== file));
        }
    },
    watch: {
        value()
        {
            this.totalProgress = 0;
        }
    }
};
</script>

<style lang="scss" scoped>
.file-input {
    display: none;
}
.progress {
    background-color: #ffffff;
    border: 1px solid #c0cadd;
}
</style>
